import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PdfLoader, PdfHighlighter, Highlight, IHighlight } from 'react-pdf-highlighter';
import 'react-pdf-highlighter/dist/style.css';
import { Box, Typography, CircularProgress, IconButton, Tooltip, Stack } from '@mui/material';
import { ZoomIn, ZoomOut, Download, Print } from '@mui/icons-material';
import { ServerHighlightGeom } from './Interface';
import { AnnotationMode, PDFDocumentProxy, PDFPageProxy, PageViewport } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  CitationHighlightProps,
  GeneratedHighlight,
  PdfDimensionMap,
  PdfDimensions,
  PDFContainer,
  ToolbarContainer,
  ViewerContainer,
  createHighlightsFromGeoJson,
  downloadDocument,
  openPdfInNewTab,
  shouldOpenPdfInline,
} from 'oncology_portal/src/utils/CitationHighlightsUtils';
import { getDocumentTypeIcon } from '../SummaryUtils';

// Internal type for the pdfjs-dist viewer instance (not exported by pdfjs-dist types)
interface PdfJsEventBus {
  on: (event: string, handler: (evt: { scale: number }) => void) => void;
  off: (event: string, handler: (evt: { scale: number }) => void) => void;
}

interface PdfJsViewer {
  currentScale: number | string;
  currentScaleValue: number | string;
  container: HTMLElement;
  eventBus: PdfJsEventBus;
  getPageView: (index: number) => { viewport: PageViewport };
}

// Promise.withResolvers polyfill for PDF.js worker (Chrome < 119, Firefox < 121, Safari < 17.4)
// Workers run in an isolated context so the polyfill in index.tsx cannot reach them.
// importScripts won't work with ES modules (.mjs), so we fetch and prepend the polyfill.
const polyfill =
  "if(typeof Promise.withResolvers==='undefined'){Promise.withResolvers=function(){let r,j;const p=new Promise((a,b)=>{r=a;j=b});return{promise:p,resolve:r,reject:j}}}\n";

let pdfWorkerBlobUrl: string | null = null;
const getPdfWorkerUrl = async (): Promise<string> => {
  if (pdfWorkerBlobUrl) return pdfWorkerBlobUrl;
  try {
    const response = await fetch(pdfWorkerUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch PDF worker');
    }
    const workerCode = await response.text();
    const blob = new Blob([polyfill + workerCode], { type: 'application/javascript' });
    pdfWorkerBlobUrl = URL.createObjectURL(blob);
    return pdfWorkerBlobUrl;
  } catch {
    return pdfWorkerUrl;
  }
};

const CitationHighlight: React.FC<CitationHighlightProps> = ({
  url,
  title = 'PDF Document',
  height = '100%',
  width = '100%',
  status,
  highlightsGeoJson,
  dateOfDocument,
  disableAnnotations = true,
  icon,
  documentType,
  isOverlay = false,
}: CitationHighlightProps) => {
  const [scale, setScale] = useState(1);
  const [debouncedScale, setDebouncedScale] = useState(1);
  const [highlights, setHighlights] = useState<GeneratedHighlight[]>([]);
  const [basePdfDimensions, setBasePdfDimensions] = useState<PdfDimensionMap>({});
  const [retryCount, setRetryCount] = useState(0);
  const [loaderKey, setLoaderKey] = useState(0);
  const [pendingScrollTarget, setPendingScrollTarget] = useState<GeneratedHighlight | null>(null);
  const [isDocumentReady, setIsDocumentReady] = useState<boolean>(false);
  const [hasSetInitialZoom, setHasSetInitialZoom] = useState<boolean>(false);
  const [workerSrc, setWorkerSrc] = useState<string | null>(null);
  const viewportToScaledRef = useRef<((rect: unknown) => unknown) | null>(null);
  const scrollViewerTo = useRef((highlight: IHighlight) => {});
  const pdfHighlighterRef = useRef<{ viewer: PdfJsViewer } | null>(null);
  const lastPdfDocumentRef = useRef<PDFDocumentProxy | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scaleListenerCleanupRef = useRef<(() => void) | null>(null);
  const scaleDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isZoomingRef = useRef<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    void getPdfWorkerUrl().then((src) => {
      if (isMounted) {
        setWorkerSrc(src);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Debounce scale changes to prevent excessive highlight regeneration
  useEffect(() => {
    if (scaleDebounceTimerRef.current) {
      clearTimeout(scaleDebounceTimerRef.current);
    }

    scaleDebounceTimerRef.current = setTimeout(() => {
      setDebouncedScale(scale);
      // Clear zooming flag after debounce period + a small buffer
      // This ensures highlights are regenerated but scroll is prevented
      setTimeout(() => {
        isZoomingRef.current = false;
      }, 50);
    }, 150); // 150ms debounce

    return () => {
      if (scaleDebounceTimerRef.current) {
        clearTimeout(scaleDebounceTimerRef.current);
      }
    };
  }, [scale]);

  // Calculate scaled dimensions for highlight generation using debounced scale
  // Use page 1 dimensions for all pages (assumes uniform page sizes)
  const scaledPdfDimensions = React.useMemo(() => {
    const page1Dims = basePdfDimensions[1];
    if (!page1Dims) {
      return null;
    }

    // Return a function that provides page 1 dimensions for any page number
    // This mimics the old behavior where all pages use the same dimensions
    const scaledDims = {
      width: page1Dims.width * debouncedScale,
      height: page1Dims.height * debouncedScale,
      rotation: page1Dims.rotation,
    };

    // Create a proxy-like object that returns page 1 dimensions for any page
    return new Proxy({} as PdfDimensionMap, {
      get: (target, prop) => {
        // Return scaled dimensions for any page number requested
        return scaledDims;
      },
    });
  }, [basePdfDimensions, debouncedScale]);

  // Helper function to parse current scale from viewer, handling various formats
  const parseCurrentScale = useCallback((viewer: PdfJsViewer): number => {
    let scale = viewer.currentScale;

    // Handle string formats
    if (typeof scale === 'string') {
      // Remove percentage sign if present
      const normalized = scale.replace('%', '').trim();
      scale = parseFloat(normalized);

      // If original value was a percentage (e.g., "100%"), convert to decimal
      if (typeof viewer.currentScaleValue === 'string' && viewer.currentScaleValue.includes('%')) {
        scale = scale / 100;
      }
    }

    // Validate and return safe default
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
  }, []);

  const handleZoomIn = useCallback(() => {
    const viewer = pdfHighlighterRef.current?.viewer;
    if (!viewer) return;

    const currentScale = parseCurrentScale(viewer);
    const newScale = Math.min(currentScale * 1.1, 3);

    isZoomingRef.current = true;
    viewer.currentScaleValue = newScale;
    setScale(newScale);
  }, [parseCurrentScale]);

  const handleZoomOut = useCallback(() => {
    const viewer = pdfHighlighterRef.current?.viewer;
    if (!viewer) return;

    const currentScale = parseCurrentScale(viewer);
    const newScale = Math.max(currentScale / 1.1, 0.2);

    isZoomingRef.current = true;
    viewer.currentScaleValue = newScale;
    setScale(newScale);
  }, [parseCurrentScale]);

  const handleDownload = useCallback(() => {
    if (!url) return;
    void downloadDocument(url);
  }, [url]);

  const handlePrint = useCallback(async () => {
    if (!url) return;

    const canOpenInline = await shouldOpenPdfInline(url);
    if (canOpenInline) {
      openPdfInNewTab(url);
      return;
    }

    await downloadDocument(url);
  }, [url]);

  // Reset state when URL changes
  useEffect(() => {
    setHighlights([]);
    setPendingScrollTarget(null);
    setIsDocumentReady(false);
    setBasePdfDimensions({});
    setScale(1);
    setHasSetInitialZoom(false);
    lastPdfDocumentRef.current = null;
    isZoomingRef.current = false;
  }, [url]);

  const generatePreciseHighlight = useCallback(
    (geojson: ServerHighlightGeom): GeneratedHighlight[] => createHighlightsFromGeoJson(geojson, scaledPdfDimensions),
    [scaledPdfDimensions],
  );

  // Check if highlight is fully visible on screen (even slightly off-screen returns false)
  const isHighlightVisible = useCallback((highlight: GeneratedHighlight | null) => {
    if (!highlight) return false;

    const viewer = pdfHighlighterRef.current?.viewer;
    const container: HTMLElement | undefined = viewer?.container;
    const rect = highlight.position.boundingRect;

    if (!container || !rect) {
      return false;
    }

    const pageElement = container.querySelector(
      `[data-page-number="${highlight.position.pageNumber}"]`,
    ) as HTMLElement | null;

    if (!pageElement) {
      return false;
    }

    // Get the actual rendered dimensions of the page (accounts for current zoom)
    const pageHeight = pageElement.clientHeight;
    const pageWidth = pageElement.clientWidth;

    // Get the base dimensions from the highlight rect
    const rectPageHeight = rect.pageHeight ?? rect.height;
    const rectPageWidth = rect.pageWidth ?? rect.width;

    // Calculate scale based on actual rendered size vs base size
    const scaleY = rectPageHeight ? pageHeight / rectPageHeight : 1;
    const scaleX = rectPageWidth ? pageWidth / rectPageWidth : 1;

    // Calculate highlight position in the actual rendered coordinates
    const highlightTop = pageElement.offsetTop + rect.y1 * scaleY;
    const highlightBottom = pageElement.offsetTop + rect.y2 * scaleY;
    const highlightLeft = pageElement.offsetLeft + rect.x1 * scaleX;
    const highlightRight = pageElement.offsetLeft + rect.x2 * scaleX;

    // Get viewport bounds
    const viewportTop = container.scrollTop;
    const viewportBottom = viewportTop + container.clientHeight;
    const viewportLeft = container.scrollLeft;
    const viewportRight = viewportLeft + container.clientWidth;

    // Add comfortable buffer zones (10% of viewport height/width)
    // Highlights too close to edges should trigger scrolling for better readability
    const verticalBuffer = container.clientHeight * 0.1;
    const horizontalBuffer = container.clientWidth * 0.1;

    // Check if highlight is comfortably visible (not too close to edges)
    const isVerticallyVisible =
      highlightTop >= viewportTop + verticalBuffer && highlightBottom <= viewportBottom - verticalBuffer;
    const isHorizontallyVisible =
      highlightLeft >= viewportLeft + horizontalBuffer && highlightRight <= viewportRight - horizontalBuffer;

    return isVerticallyVisible && isHorizontallyVisible;
  }, []);

  useEffect(() => {
    if (highlightsGeoJson && scaledPdfDimensions) {
      const newHighlights = highlightsGeoJson.flatMap((highlight: ServerHighlightGeom) =>
        generatePreciseHighlight(highlight),
      );

      setHighlights(newHighlights);

      // Only set scroll target if we're not currently zooming
      // This prevents auto-scroll when user zooms in/out
      if (newHighlights.length > 0 && !isZoomingRef.current) {
        const sortedHighlights = [...newHighlights].sort((a, b) => {
          if (a.position.pageNumber !== b.position.pageNumber) {
            return a.position.pageNumber - b.position.pageNumber;
          }

          const aY = a.position.boundingRect?.y1 ?? 0;
          const bY = b.position.boundingRect?.y1 ?? 0;
          return aY - bY;
        });

        const candidate = sortedHighlights.find((highlight) => {
          const rect = highlight.position.boundingRect;
          if (!rect) return false;

          const pageHeight = rect.pageHeight ?? rect.height;
          if (!pageHeight) return false;

          return rect.y1 <= pageHeight * 0.5;
        });

        const targetHighlight = candidate ?? sortedHighlights[0];

        if (targetHighlight) {
          setPendingScrollTarget(targetHighlight);
        }
      }
    } else if (!highlightsGeoJson) {
      setHighlights([]);
      setPendingScrollTarget(null);
    }
  }, [highlightsGeoJson, scaledPdfDimensions, generatePreciseHighlight]);

  // Handle pending scroll - only scroll if highlight is not visible
  useEffect(() => {
    if (
      pendingScrollTarget &&
      scrollViewerTo.current &&
      typeof scrollViewerTo.current === 'function' &&
      isDocumentReady
    ) {
      let isMounted = true;
      let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

      const attemptScroll = () => {
        if (
          !isMounted ||
          !pendingScrollTarget ||
          !scrollViewerTo.current ||
          typeof scrollViewerTo.current !== 'function' ||
          !isDocumentReady
        ) {
          return;
        }

        try {
          // Check if highlight is already visible on screen
          if (isHighlightVisible(pendingScrollTarget)) {
            setPendingScrollTarget(null);
            return;
          }

          // Highlight is not visible, scroll to it
          scrollViewerTo.current(pendingScrollTarget);
          setPendingScrollTarget(null);
        } catch (e) {
          // Single retry after a short delay
          retryTimeoutId = setTimeout(() => {
            if (
              !isMounted ||
              !pendingScrollTarget ||
              !scrollViewerTo.current ||
              typeof scrollViewerTo.current !== 'function' ||
              !isDocumentReady
            ) {
              return;
            }

            try {
              if (isHighlightVisible(pendingScrollTarget)) {
                setPendingScrollTarget(null);
                return;
              }

              scrollViewerTo.current(pendingScrollTarget);
              setPendingScrollTarget(null);
            } catch (retryError) {}
          }, 300);
        }
      };

      const timeoutId = setTimeout(attemptScroll, 150);

      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
        }
      };
    }
  }, [pendingScrollTarget, highlights, isDocumentReady, isHighlightVisible]);

  const onSelectionFinished = useCallback(() => {
    return null;
  }, []);

  const LoadingComponent = () => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={3}>
      <CircularProgress size={48} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        Loading PDF...
      </Typography>
    </Box>
  );

  const highlightTransform = useCallback(
    // react-pdf-highlighter's T_ViewportHighlight has an internal position type incompatible
    // with GeneratedHighlight's PDFRect — using IHighlight as the common base
    (highlight: IHighlight, index: number, viewportToScaledFn: (rect: unknown) => unknown, isScrolledTo: boolean) => {
      if (!viewportToScaledRef.current) {
        viewportToScaledRef.current = viewportToScaledFn;
      }

      return (
        <Highlight
          key={index}
          isScrolledTo={isScrolledTo}
          position={highlight.position as any}
          comment={highlight.comment}
          onClick={() => {}}
          onMouseOver={() => {}}
          onMouseOut={() => {}}
        />
      );
    },
    [],
  );

  // Apply initial zoom when document is ready
  useEffect(() => {
    if (isDocumentReady && !hasSetInitialZoom) {
      let isMounted = true;
      let retryCount = 0;
      const MAX_RETRIES = 10;

      const applyInitialZoom = () => {
        if (!isMounted || retryCount >= MAX_RETRIES) {
          return;
        }

        const viewer = pdfHighlighterRef.current?.viewer;
        if (!viewer) {
          retryCount++;
          setTimeout(applyInitialZoom, 100);
          return;
        }

        try {
          // Set to 100% zoom (scale 1) for overlay mode, page-width for side panel
          const initialScale = isOverlay ? 1 : 'page-width';
          viewer.currentScaleValue = initialScale;

          // Wait for the viewer to process the scale change
          requestAnimationFrame(() => {
            if (!isMounted) return;
            const currentScale = parseCurrentScale(viewer);
            setScale(currentScale);
            setHasSetInitialZoom(true);
          });
        } catch (e) {
          if (!isMounted) return;
          setScale(1);
          setHasSetInitialZoom(true);
        }
      };

      // Start attempting to apply zoom after a short delay
      const timeoutId = setTimeout(applyInitialZoom, 300);
      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
      };
    }
  }, [isDocumentReady, hasSetInitialZoom, parseCurrentScale, isOverlay]);

  // Keep scale in sync
  useEffect(() => {
    let retryCount = 0;
    const MAX_RETRIES = 10;

    const setupScaleListener = () => {
      if (retryCount >= MAX_RETRIES) {
        return;
      }

      const viewer = pdfHighlighterRef.current?.viewer;
      const eventBus = viewer?.eventBus;
      if (!viewer || !eventBus) {
        retryCount++;
        setTimeout(setupScaleListener, 100);
        return;
      }

      const handleScaleChange = (evt: { scale: number }) => {
        const newScaleRaw = evt?.scale ?? viewer.currentScale;
        const newScale = typeof newScaleRaw === 'string' ? parseFloat(newScaleRaw) : newScaleRaw;

        if (typeof newScale === 'number' && !Number.isNaN(newScale) && Number.isFinite(newScale)) {
          setScale((current) => {
            if (Math.abs(current - newScale) > 0.001) {
              return newScale;
            }
            return current;
          });
        } else {
          setScale(Number(viewer.currentScale) || 1);
        }
      };

      eventBus.on('scalechanging', handleScaleChange);
      eventBus.on('scalechange', handleScaleChange);

      scaleListenerCleanupRef.current = () => {
        try {
          if (eventBus && eventBus.off) {
            eventBus.off('scalechanging', handleScaleChange);
            eventBus.off('scalechange', handleScaleChange);
          }
        } catch (e) {}
      };
    };

    setupScaleListener();

    return () => {
      if (scaleListenerCleanupRef.current) {
        scaleListenerCleanupRef.current();
        scaleListenerCleanupRef.current = null;
      }
    };
  }, []);

  // Determine which icon to display: use provided icon or derive from documentType
  const displayIcon = icon || getDocumentTypeIcon(documentType);

  return (
    <ViewerContainer sx={{ height, width, backgroundColor: isOverlay ? 'grey.900' : 'background.paper' }}>
      <ToolbarContainer
        sx={{
          padding: '12px 24px',
          height: '64px',
          borderBottom: 'none',
          flexShrink: 0,
          backgroundColor: isOverlay ? 'grey.900' : 'background.paper',
          color: isOverlay ? 'primary.contrastText' : 'text.primary',
        }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
          {displayIcon && (
            <Box
              aria-hidden="true"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 35,
                minHeight: 35,
                flexShrink: 0,

                '& .MuiSvgIcon-root': {
                  fontSize: '35px',
                  color: 'inherit',
                },
              }}>
              {displayIcon}
            </Box>
          )}
          <Stack direction="column" alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }} color="inherit">
              {title}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 400 }} color="inherit">
              {status && `${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`}
              {status && dateOfDocument && ' | '}
              {dateOfDocument && `Date: ${dateOfDocument}`}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} disabled={scale <= 0.2} color="inherit">
              <ZoomOut />
            </IconButton>
          </Tooltip>

          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', px: 1 }} color="inherit">
            {Math.round(scale * 100)}%
          </Typography>

          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} disabled={scale >= 3} color="inherit">
              <ZoomIn />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download PDF">
            <IconButton onClick={handleDownload} color="inherit">
              <Download />
            </IconButton>
          </Tooltip>

          <Tooltip title="Print PDF">
            <IconButton onClick={handlePrint} color="inherit">
              <Print />
            </IconButton>
          </Tooltip>
        </Stack>
      </ToolbarContainer>

      <PDFContainer className="pdf-container">
        {!workerSrc ? (
          <LoadingComponent />
        ) : (
          <PdfLoader
            key={`${loaderKey}-${url}`}
            url={url || ''}
            workerSrc={workerSrc}
            beforeLoad={<LoadingComponent />}
            errorMessage={
              <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={3}>
                <Typography variant="body1" color="error">
                  Failed to load PDF
                </Typography>
              </Box>
            }
            onError={() => {
              if (retryCount < 4) {
                if (retryTimeoutRef.current) {
                  clearTimeout(retryTimeoutRef.current);
                }
                retryTimeoutRef.current = setTimeout(() => {
                  setRetryCount((c) => c + 1);
                  setLoaderKey((k) => k + 1);
                }, 100);
              }
            }}
            {...({ isOffscreenCanvasSupported: false } as any)}>
            {(pdfDocument: PDFDocumentProxy) => {
              // Reset retry count on successful load
              if (retryCount > 0) {
                setTimeout(() => setRetryCount(0), 0);
              }

              // Initialize document only once per unique pdfDocument instance
              if (pdfDocument && lastPdfDocumentRef.current !== pdfDocument) {
                lastPdfDocumentRef.current = pdfDocument;
                setIsDocumentReady(false);
                setHighlights([]);
                setPendingScrollTarget(null);
                setBasePdfDimensions({});

                try {
                  // Assumes all pages have the same dimensions (standard for most PDFs)
                  pdfDocument.getPage(1).then((page: PDFPageProxy) => {
                    const viewport = page.getViewport({ scale: 1 });
                    const dims: PdfDimensions = {
                      width: viewport.width,
                      height: viewport.height,
                      rotation: viewport.rotation || 0,
                    };

                    // Set dimensions for page 1 - will be used for all pages
                    setBasePdfDimensions({ 1: dims });

                    setTimeout(() => {
                      try {
                        setIsDocumentReady(true);

                        if (!highlightsGeoJson || highlightsGeoJson.length === 0) {
                          setTimeout(() => {
                            const viewer = pdfHighlighterRef.current?.viewer;
                            if (viewer && viewer.container) {
                              viewer.container.scrollTop = 0;
                            }
                          }, 100);
                        }
                      } catch (e) {
                        setTimeout(() => setIsDocumentReady(true), 500);
                      }
                    }, 200);
                  });
                } catch (e) {
                  setTimeout(() => setIsDocumentReady(true), 1000);
                }
              }

              // Show loading until document is fully ready
              // This prevents double rendering of the PDF
              if (!isDocumentReady || lastPdfDocumentRef.current !== pdfDocument) {
                return <LoadingComponent />;
              }

              // Only render PdfHighlighter when document is ready and initialized
              return (
                <PdfHighlighter
                  ref={pdfHighlighterRef}
                  pdfDocument={pdfDocument}
                  pdfScaleValue={hasSetInitialZoom ? scale.toString() : isOverlay ? '1' : 'page-width'}
                  enableAreaSelection={(event: MouseEvent) => event.altKey}
                  onScrollChange={() => {}}
                  scrollRef={(scrollTo: (highlight: IHighlight) => void) => {
                    scrollViewerTo.current = scrollTo;
                  }}
                  onSelectionFinished={onSelectionFinished}
                  highlightTransform={highlightTransform}
                  highlights={highlights}
                  {...({
                    pdfViewerOptions: {
                      annotationMode: disableAnnotations ? AnnotationMode.DISABLE : AnnotationMode.ENABLE_FORMS,
                    },
                  } as any)}
                />
              );
            }}
          </PdfLoader>
        )}
      </PDFContainer>
    </ViewerContainer>
  );
};

export default CitationHighlight;
